export type QueueItemStatus = 'queued' | 'uploading' | 'done' | 'failed';

export interface QueueItemUpdate<R> {
  status: 'done' | 'failed';
  result?: R;
  error?: string;
}

export interface RunQueueOptions<T, R> {
  concurrency: number;
  worker: (payload: T, signal: AbortSignal) => Promise<R>;
  maxRetries?: number;
  retryDelayMs?: number;
  shouldRetry?: (err: unknown) => boolean;
  onItemStart?: (id: string) => void;
  onItemEnd?: (id: string, update: QueueItemUpdate<R>) => void;
  signal?: AbortSignal;
}

export interface QueuePayload<T> {
  id: string;
  payload: T;
}

export async function runQueue<T, R>(
  items: ReadonlyArray<QueuePayload<T>>,
  opts: RunQueueOptions<T, R>,
): Promise<void> {
  const {
    concurrency,
    worker,
    maxRetries = 1,
    retryDelayMs = 1000,
    shouldRetry,
    onItemStart,
    onItemEnd,
    signal,
  } = opts;

  const pending = items.slice();

  async function processOne(item: QueuePayload<T>): Promise<void> {
    onItemStart?.(item.id);
    let attempt = 0;
    while (true) {
      if (signal?.aborted) {
        onItemEnd?.(item.id, { status: 'failed', error: 'cancelled' });
        return;
      }
      attempt += 1;
      try {
        const ac = new AbortController();
        const onAbort = () => ac.abort();
        signal?.addEventListener('abort', onAbort);
        try {
          const result = await worker(item.payload, ac.signal);
          onItemEnd?.(item.id, { status: 'done', result });
          return;
        } finally {
          signal?.removeEventListener('abort', onAbort);
        }
      } catch (err) {
        if (signal?.aborted) {
          onItemEnd?.(item.id, { status: 'failed', error: 'cancelled' });
          return;
        }
        const retryable = shouldRetry ? shouldRetry(err) : true;
        if (!retryable || attempt > maxRetries) {
          const message = err instanceof Error ? err.message : String(err);
          onItemEnd?.(item.id, { status: 'failed', error: message });
          return;
        }
        await new Promise((r) => setTimeout(r, retryDelayMs * attempt));
      }
    }
  }

  async function workerLoop(): Promise<void> {
    while (pending.length > 0) {
      if (signal?.aborted) return;
      const next = pending.shift();
      if (!next) return;
      await processOne(next);
    }
  }

  const slots = Array.from({ length: Math.max(1, concurrency) }, () => workerLoop());
  await Promise.all(slots);
}
